const { fetchJSON } = window.BAO.Utils

class LocaleSwitcher extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.isOpen = false

    this.currentCountry = window.theme?.currentCountry ?? 'US'

    this.defaultCountryCode = this.currentCountry
    this.validCountryCodes = ['US', 'CA']
    this.defaultLocale = 'en'
    this.validLocales = ['en', 'fr']

    /* ?country= in URL check - Start */
    // eslint-disable-next-line compat/compat
    const params = new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop) => searchParams.get(prop),
    })

    this.countryParam = this.validCountryCodes.includes(params.country)
      ? params.country
      : this.defaultCountryCode

    if (this.countryParam !== null) {
      this.setStorageFromURL(this.countryParam)
    }
    /* ?country= in URL check - End */

    if (this.countryParam !== null) {
      const countryCode = this.countryParam

      this.setupCountrySwitcher(countryCode)
      this.updateToggleCountry(countryCode)
    } else if (!this.noLocationStored) {
      let countryCode = window.localStorage.getItem(this.userLocationName)

      countryCode = this.validCountryCodes.includes(countryCode)
        ? countryCode
        : this.defaultCountryCode

      this.setupCountrySwitcher(countryCode)
      this.updateToggleCountry(countryCode)
    } else {
      fetchJSON('/browsing_context_suggestions.json', {
        method: 'GET',
      }).then((res) => {
        let countryCode = res.detected_values.country.handle

        countryCode = this.validCountryCodes.includes(countryCode)
          ? countryCode
          : this.defaultCountryCode

        this.setupCountrySwitcher(countryCode)
        this.updateToggleCountry(countryCode)
      })
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onButtonClick = this.onButtonClick.bind(this)
    this.onConfirmClick = this.onConfirmClick.bind(this)
    this.onToggleClick = this.onToggleClick.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.onOverlayMouseOver = this.onOverlayMouseOver.bind(this)
    this.onOverlayClick = this.onOverlayClick.bind(this)
    this.onBodyClick = this.onBodyClick.bind(this)
    this.onSearchOpen = this.onSearchOpen.bind(this)

    this.listeners.add(this, 'click', this.onButtonClick)

    if (this.els?.confirm?.exists) {
      this.els.confirm.elements.forEach((confirmEl) => {
        this.listeners.add(confirmEl, 'click', this.onConfirmClick)
      })
    }

    if (this.externalEls?.toggle?.exists) {
      this.externalEls.toggle.elements.forEach((toggleEl) => {
        this.listeners.add(toggleEl, 'click', this.onToggleClick)
      })
    }

    if (this.els?.close?.exists) {
      this.els.close.elements.forEach((closeEl) => {
        this.listeners.add(closeEl, 'click', () => this.close())
      })
    }

    if (this.externalEls?.overlay?.exists) {
      this.externalEls.overlay.elements.forEach((overlayEl) => {
        this.listeners.add(overlayEl, 'click', this.onOverlayClick)
        this.listeners.add(overlayEl, 'mouseover', this.onOverlayMouseOver)
      })
    }

    if (this.externalEls?.dialog?.exists) {
      this.listeners.add(document.body, 'click', this.onBodyClick)

      this.listeners.add(this.externalEls.dialog.element, 'cancel', (event) => {
        event.preventDefault()
        this.close()
      })
    }

    this.listeners.add(document, 'keydown', this.onKeyDown)
    this.listeners.add(document, 'search:open', this.onSearchOpen)
  }

  onSearchOpen () {
    if (!this.isOpen || !window.BAO.MediaBreakpoints.isLg) return

    this.close()
  }

  onBodyClick (event) {
    if (!this.isOpen) return

    const target = event.target

    if (target.matches(this.externalEls.dialog.selector)) {
      this.close()
    }
  }

  setStorageFromURL (countryCode) {
    window.localStorage.setItem(this.userLocationName, countryCode)
  }

  onToggleClick () {
    return this.isOpen ? this.close() : this.open()
  }

  open () {
    if (this.externalEls?.dialog?.exists !== true) return

    document.dispatchEvent(new Event('search:close'))

    this.addMenuClasses()

    const dialog = this.externalEls.dialog.element

    if (!dialog.hasAttribute('open')) {
      if (window.BAO.MediaBreakpoints.isLg) {
        dialog.show()
      } else {
        dialog.showModal()
      }
    }

    this.isOpen = true
  }

  close () {
    if (this.externalEls?.dialog?.exists !== true) return

    this.removeMenuClasses()

    const dialog = this.externalEls.dialog.element

    if (dialog.hasAttribute('open')) {
      dialog.close()
    }

    this.isOpen = false
  }

  addMenuClasses () {
    document.body.classList.add(
      'util-SiteOverflowed',
      'lyt-Theme-localeSwitcherOpen',
    )

    if (!document.body.classList.contains('lyt-Theme-localeSwitcherActive')) {
      this._menuTimer = setTimeout(() => {
        document.body.classList.add('lyt-Theme-localeSwitcherActive')
        document.dispatchEvent(new Event('drawers:close'))
      }, 400)
    } else {
      document.dispatchEvent(new Event('drawers:close'))
    }
  }

  removeMenuClasses () {
    clearTimeout(this._menuTimer)

    document.body.classList.remove('lyt-Theme-localeSwitcherActive')

    this._menuTimer = setTimeout(() => {
      document.body.classList.remove(
        'util-SiteOverflowed',
        'lyt-Theme-localeSwitcherOpen',
      )
    }, 200)
  }

  onOverlayClick () {
    if (!this.isOpen || window.BAO.MediaBreakpoints.isLg) return

    this.close()
  }

  onOverlayMouseOver () {
    if (!this.isOpen || !window.BAO.MediaBreakpoints.isLg) return

    this.close()
  }

  onKeyDown (event) {
    if (!this.isOpen) return
    if (event.code?.toUpperCase() === 'ESCAPE') this.close()
  }

  onConfirmClick () {
    const currentButton = document.querySelector(
      `${this.els?.button?.selector}[aria-pressed="true"]`,
    )

    if (!currentButton) return

    const locale = this.selectedLocale ?? 'en'
    const countryCode = currentButton.dataset.countryCode.toLowerCase()
    const fullLocale = `${locale}-${countryCode}`
    const localePath = `/${fullLocale}`

    const base = `${currentButton.dataset.siteUrl}${localePath}${this.currentPageUrl}`
    const url = new URL(base)
    url.searchParams.set('country', currentButton.dataset.countryCode)
    const href = url.toString()

    if (window.location.href === href) {
      window.location.reload()
    } else {
      window.location.href = href
    }
  }

  onButtonClick (event) {
    event.preventDefault()

    const buttonSelector = '[data-locale-switcher-el="button"]'

    if (!event.target.closest(buttonSelector)) return

    const clickedButton = event.target.closest(buttonSelector)
    const allButtons = document.querySelectorAll(buttonSelector)

    allButtons.forEach((button) => {
      button.removeAttribute('aria-pressed')
    })

    clickedButton.setAttribute('aria-pressed', 'true')

    const countryCode = clickedButton.dataset.countryCode

    const languages = this.countries[countryCode]?.languages ?? [
      {
        name: 'English',
        locale: 'en',
      },
    ]

    this.renderLanguagePicker(countryCode, languages, null)
    this.renderCurrentCountryLabel(countryCode)

    this.scrollTo({ top: this.scrollHeight, behavior: 'smooth' })
  }

  renderCurrentCountryLabel (countryCode) {
    const container = this.els.text.element

    container.innerHTML = ''

    const country = this.countries[countryCode.toUpperCase()]

    if (!country) return

    container.innerHTML = `${country.name} (${country.currency})`
  }

  renderLanguagePicker (countryCode, languages, activeLocale = null) {
    const container = this.els.language.element

    const confirmEl = this.els.confirm.element

    container.innerHTML = ''

    const resolvedLocale = activeLocale ?? languages[0].locale

    this.selectedLocale = resolvedLocale

    confirmEl.disabled = false

    const label = document.createElement('p')

    label.className = `hd-LocaleSwitcher_LanguageTitle fz-20_120`
    label.textContent = this.dataset.selectLanguageText

    const picker = document.createElement('ul')

    picker.className = `hd-LocaleSwitcher_LanguageItems`

    languages.forEach(({ name, locale }) => {
      const isActive = locale === resolvedLocale

      const li = document.createElement('li')

      li.className = `hd-LocaleSwitcher_LanguageItem`

      li.innerHTML = `
        <button
          type="button"
          class="hd-LocaleSwitcher_LanguageButton fz-14_120"
          data-locale="${locale}"
          ${isActive ? 'aria-pressed="true"' : ''}
        >
          ${name}
        </button>
      `

      li.querySelector('button').addEventListener('click', (event) => {
        picker
          .querySelectorAll('button')
          .forEach((button) => button.removeAttribute('aria-pressed'))

        event.currentTarget.setAttribute('aria-pressed', 'true')

        this.selectedLocale = locale

        confirmEl.disabled = false
      })

      picker.appendChild(li)
    })

    container.appendChild(label)
    container.appendChild(picker)
  }

  updateToggleCountry (countryCode, locale = null) {
    const country = this.countries[countryCode.toUpperCase()]

    if (!country) return

    const renderCountrySelects = () => {
      const openButtons = document.querySelectorAll(
        '[data-locale-switcher-external-el="open"]',
      )

      if (openButtons.length) {
        openButtons.forEach((el) => {
          this.listeners.add(el, 'click', () => this.onToggleClick())
        })
      }

      const countrySelects = document.querySelectorAll(
        '[data-locale-switcher-external-el="country-select"]',
      )

      if (countrySelects.length) {
        countrySelects.forEach((el) => {
          el.innerHTML = `
          <span class="lcl-SwitcherOpen_Flag">
            <img
              src="https://cdn.shopify.com/static/images/flags/${countryCode.toLowerCase()}.svg"
              class="lcl-SwitcherOpen_FlagImage"
              width="20"
              height="15"
              alt=""
              aria-hidden="true"
            />
          </span>

          <span class="lcl-SwitcherOpen_CountryText fz-14_140">${country.name} <span class="lcl-SwitcherOpen_Currency">(${country.currency})</span></span>
        `
        })
      }
    }

    if (theme.mobileMenuLoaded === false) {
      document.addEventListener('mobile-menu:loaded', renderCountrySelects, {
        once: true,
      })
    } else {
      renderCountrySelects()
    }
  }

  setupCountrySwitcher (countryCode) {
    this.createOptions(countryCode)

    window.localStorage.setItem(this.userLocationName, countryCode)

    const upperCode = countryCode.toUpperCase()

    const languages = this.countries[upperCode]?.languages ?? [
      {
        name: 'English',
        locale: 'en',
      },
    ]

    let currentLocale = window.Shopify?.locale ?? 'en'

    if (!this.validLocales.includes(currentLocale)) {
      currentLocale = this.defaultLocale
    }

    this.selectedLocale = currentLocale

    this.renderCurrentCountryLabel(upperCode)

    this.renderLanguagePicker(upperCode, languages, currentLocale)

    if (!this.noLocationStored) return
    if (this.countryParam !== null) return

    document.body.classList.add('util-SiteOverflowed')
  }

  createOptions (countryCode) {
    const grouped = Object.entries(this.countries).reduce(
      (acc, [key, value]) => {
        const group = value.group || 'Other'

        if (!acc[group]) {
          acc[group] = []
        }

        acc[group].push([key, value])

        return acc
      },
      {},
    )

    const groupOrder = ['North America']

    const sortedGroups = [
      ...groupOrder.filter((group) => grouped[group]),
      ...Object.keys(grouped).filter((group) => !groupOrder.includes(group)),
    ]

    let drawerHTML = ''
    let accordionOpen = true

    sortedGroups.forEach((groupName) => {
      const countries = grouped[groupName]

      const groupId = groupName.toLowerCase().replace(/\s+/g, '-')

      const drawerItems = countries
        .map(([key, value]) => {
          const isCurrentSite = key.toLowerCase() === countryCode.toLowerCase()

          return this.createDrawerElement(
            key,
            value.site,
            isCurrentSite,
            value.name,
          )
        })
        .join('')

      drawerHTML += this.createAccordionGroup(
        groupName,
        groupId,
        drawerItems,
        accordionOpen,
      )

      accordionOpen = false
    })

    this.els.options.element.innerHTML = drawerHTML
  }

  createAccordionGroup (groupName, groupId, itemsHTML, isOpen = false) {
    const panelId = `locale-switcher-hd-${groupId}`

    return `
      <accordion-group class="hd-LocaleSwitcher_Accordion">
        <button
          type="button"
          class="hd-LocaleSwitcher_AccordionTitle fz-20_120"
          aria-controls="${panelId}"
          aria-expanded="${isOpen}"
          data-accordion-group-el="trigger"
        >
          ${groupName}

          <div class="hd-LocaleSwitcher_AccordionIcons">
            <div class="hd-LocaleSwitcher_AccordionIcon hd-LocaleSwitcher_AccordionIcon-open">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                <path stroke="currentColor" stroke-linejoin="round" stroke-width="1.5" d="m5 8 5 5 5-5"/>
              </svg>
            </div>

            <div class="hd-LocaleSwitcher_AccordionIcon hd-LocaleSwitcher_AccordionIcon-close">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                <path stroke="currentColor" stroke-linejoin="round" stroke-width="1.5" d="m5 8 5 5 5-5"/>
              </svg>
            </div>
          </div>
        </button>

        <div
          class="hd-LocaleSwitcher_AccordionBody"
          aria-hidden="${!isOpen}"
          id="${panelId}"
          data-accordion-group-el="content"
        >
          <div class="hd-LocaleSwitcher_AccordionContent">
            <div class="hd-LocaleSwitcher_AccordionItems">
              ${itemsHTML}
            </div>
          </div>
        </div>
      </accordion-group>
    `
  }

  createDrawerElement (code, site, isCurrentSite, country) {
    const countryConfig = this.countries[code]
    const siteUrl = countryConfig?.externalUrl ?? this.selectedSites[site]?.url

    if (!siteUrl) return ''

    const flagCode = code.toLowerCase()

    return `
    <div class="hd-LocaleSwitcher_AccordionItem">
      <button
        class="hd-LocaleSwitcher_AccordionButton fz-14_120"
        aria-pressed="${isCurrentSite ? 'true' : 'false'}"
        data-locale-switcher-el="button"
        data-country-code="${code}"
        data-site-url="${siteUrl}"
      >
        <img
          class="hd-LocaleSwitcher_AccordionFlag"
          src="https://cdn.shopify.com/static/images/flags/${flagCode}.svg"
          width="20"
          height="15"
          alt=""
          aria-hidden="true"
        />

        <span class="hd-LocaleSwitcher_LinkName">
          ${country}
        </span>
      </button>
    </div>
  `
  }

  get currentPageUrl () {
    const path = window.location.pathname.replace(
      /^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/,
      '',
    )

    const params = new URLSearchParams(window.location.search)
    params.delete('country')

    const qs = params.toString()

    return qs ? `${path}?${qs}` : path
  }

  get noLocationStored () {
    return (
      window.localStorage.getItem(this.userLocationName) == null ||
      (window.localStorage.getItem(this.userLocationName) === undefined &&
        !window.Shopify.designMode)
    )
  }

  get userLocationName () {
    return 'BAO-localization'
  }

  get selectedSites () {
    return this.sites
  }

  get sites () {
    return {
      NA: {
        url: `https://${window.theme.isStaging === true ? 'staging.' : ''}anonoptics.com`,
      },
    }
  }

  get countries () {
    return window.theme.countries
  }
}

if (!customElements.get('locale-switcher')) {
  customElements.define('locale-switcher', LocaleSwitcher)
}
